import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CreditCard, Lock, Loader2, Check, Package, Truck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INSTALLMENT_OPTIONS = [
  { count: 1, label: "Tek Çekim", rate: 0 },
  { count: 2, label: "2 Taksit", rate: 7 },
  { count: 3, label: "3 Taksit", rate: 8.64 },
  { count: 4, label: "4 Taksit", rate: 10.64 },
  { count: 5, label: "5 Taksit", rate: 12.97 },
  { count: 6, label: "6 Taksit", rate: 15.41 },
  { count: 7, label: "7 Taksit", rate: 17.95 },
  { count: 8, label: "8 Taksit", rate: 20.61 },
  { count: 9, label: "9 Taksit", rate: 23.4 },
  { count: 10, label: "10 Taksit", rate: 26.31 },
  { count: 11, label: "11 Taksit", rate: 29.37 },
  { count: 12, label: "12 Taksit", rate: 32.57 },
];

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderNumber = searchParams.get("order");
  const baseAmount = parseFloat(searchParams.get("amount")) || 0;
  const sessionToken = searchParams.get("token");

  const [processing, setProcessing] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(1);
  const [orderData, setOrderData] = useState({ items: [], cargo_fee: 0, is_free_shipping: false, total_amount: 0 });
  const [loadingItems, setLoadingItems] = useState(true);
  const [cardData, setCardData] = useState({
    cardHolderName: "",
    cardNumber: "",
    expMonth: "",
    expYear: "",
    cvv: "",
    use3D: true
  });

  useEffect(() => {
    if (orderNumber) {
      fetchOrderDetails();
    }
  }, [orderNumber]);

  const fetchOrderDetails = async () => {
    try {
      const response = await axios.get(`${API}/payment/order-details/${orderNumber}`);
      if (response.data) {
        setOrderData(response.data);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoadingItems(false);
    }
  };

  const productTotal = orderData.total_amount || 0;
  const cargoFee = orderData.cargo_fee || 0;
  const isFreeShipping = orderData.is_free_shipping || false;
  const orderTotal = orderData.final_total || baseAmount;

  const getInstallmentDetails = (count) => {
    const option = INSTALLMENT_OPTIONS.find(o => o.count === count);
    if (!option) return { total: orderTotal, monthly: orderTotal, rate: 0 };
    const total = orderTotal * (1 + option.rate / 100);
    const monthly = total / count;
    return { total, monthly, rate: option.rate };
  };

  const currentInstallment = getInstallmentDetails(selectedInstallment);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(" ") : value;
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, "").length <= 16) {
      setCardData({ ...cardData, cardNumber: formatted });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cardData.cardHolderName || !cardData.cardNumber || !cardData.expMonth || !cardData.expYear || !cardData.cvv) {
      toast.error("Lütfen tüm kart bilgilerini doldurun");
      return;
    }

    const cardNum = cardData.cardNumber.replace(/\s/g, "");
    if (cardNum.length < 15) {
      toast.error("Geçersiz kart numarası");
      return;
    }

    if (cardData.cvv.length < 3) {
      toast.error("Geçersiz CVV");
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/payment/process`, {
        order_number: orderNumber,
        session_token: sessionToken,
        card_holder_name: cardData.cardHolderName,
        card_number: cardNum,
        exp_month: cardData.expMonth,
        exp_year: cardData.expYear,
        cvv: cardData.cvv,
        use_3d: cardData.use3D,
        installment_count: selectedInstallment,
        total_amount: currentInstallment.total
      });

      if (response.data.success) {
        if (response.data.redirect_url) {
          window.location.href = response.data.redirect_url;
        } else {
          toast.success("Ödeme başarılı!");
          navigate(`/checkout/success?order_id=${orderNumber}`);
        }
      } else {
        toast.error(response.data.message || "Ödeme başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ödeme işlemi başarısız");
    } finally {
      setProcessing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(price);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 12 }, (_, i) => currentYear + i);
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));

  if (!orderNumber || !baseAmount) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <p className="text-red-500">Geçersiz ödeme oturumu</p>
            <Button onClick={() => navigate("/cart")} className="mt-4">Sepete Dön</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8" data-testid="payment-page">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Lock className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-600 font-medium">256-bit SSL Güvenli Ödeme</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Ödeme</h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-orange-500" />
                  Kredi Kartı / Banka Kartı
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="cardHolderName">Kart Üzerindeki İsim</Label>
                    <Input id="cardHolderName" placeholder="AD SOYAD" value={cardData.cardHolderName} onChange={(e) => setCardData({ ...cardData, cardHolderName: e.target.value.toUpperCase() })} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="cardNumber">Kart Numarası</Label>
                    <Input id="cardNumber" placeholder="0000 0000 0000 0000" value={cardData.cardNumber} onChange={handleCardNumberChange} className="mt-1 font-mono text-lg" maxLength={19} />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Ay</Label>
                      <Select value={cardData.expMonth} onValueChange={(v) => setCardData({ ...cardData, expMonth: v })}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Ay" /></SelectTrigger>
                        <SelectContent>{months.map((month) => (<SelectItem key={month} value={month}>{month}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Yıl</Label>
                      <Select value={cardData.expYear} onValueChange={(v) => setCardData({ ...cardData, expYear: v })}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Yıl" /></SelectTrigger>
                        <SelectContent>{years.map((year) => (<SelectItem key={year} value={String(year)}>{year}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="cvv">CVV</Label>
                      <Input id="cvv" type="password" placeholder="***" value={cardData.cvv} onChange={(e) => setCardData({ ...cardData, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="mt-1" maxLength={4} />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 pt-2">
                    <Checkbox id="use3D" checked={cardData.use3D} onCheckedChange={(checked) => setCardData({ ...cardData, use3D: checked })} />
                    <label htmlFor="use3D" className="text-sm text-slate-600">3D Secure ile güvenli ödeme yap</label>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Taksit Seçenekleri</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {INSTALLMENT_OPTIONS.map((option) => {
                    const details = getInstallmentDetails(option.count);
                    const isSelected = selectedInstallment === option.count;
                    return (
                      <button key={option.count} type="button" onClick={() => setSelectedInstallment(option.count)} className={`relative p-3 rounded-lg border-2 transition-all ${isSelected ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
                        {isSelected && <div className="absolute top-1 right-1"><Check className="h-3 w-3 text-orange-500" /></div>}
                        <div className="text-center">
                          <div className={`font-bold ${isSelected ? "text-orange-600" : "text-slate-900"}`}>{option.count === 1 ? "Peşin" : option.count + " Taksit"}</div>
                          <div className="text-xs text-slate-500 mt-1">{formatPrice(details.monthly)}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {selectedInstallment > 1 && (
                  <div className="mt-4 bg-slate-50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{selectedInstallment} Taksit</span>
                      <div className="text-right">
                        <div className="font-bold text-orange-600">{formatPrice(currentInstallment.monthly)} x {selectedInstallment}</div>
                        <div className="text-sm text-slate-500">Toplam: {formatPrice(currentInstallment.total)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Package className="h-5 w-5" />Sipariş Özeti</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Sipariş No</p>
                  <p className="font-mono font-bold text-sm">{orderNumber}</p>
                </div>

                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {loadingItems ? (
                    <div className="text-center py-4"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
                  ) : orderData.items?.length > 0 ? (
                    orderData.items.map((item, index) => (
                      <div key={index} className="flex gap-3 pb-3 border-b last:border-0">
                        {item.image && <img src={item.image} alt={item.name} className="w-10 h-10 object-contain rounded bg-white border" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-900 line-clamp-2">{item.name}</p>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-slate-500">{item.quantity} adet</span>
                            <span className="text-xs font-semibold text-orange-600">{formatPrice(item.price * item.quantity)}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : null}
                </div>

                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Ürün Toplamı</span>
                    <span>{formatPrice(productTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 flex items-center gap-1"><Truck className="h-4 w-4" />Kargo</span>
                    {isFreeShipping ? (
                      <span className="text-green-600 font-medium">Ücretsiz</span>
                    ) : (
                      <span>{formatPrice(cargoFee)}</span>
                    )}
                  </div>
                  {selectedInstallment > 1 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Taksit Farkı</span>
                      <span className="text-slate-500">+{formatPrice(currentInstallment.total - orderTotal)}</span>
                    </div>
                  )}
                </div>

                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">Toplam</span>
                    <span className="text-lg font-bold text-orange-600">{formatPrice(currentInstallment.total)}</span>
                  </div>
                  {selectedInstallment > 1 && <div className="text-sm text-slate-500 text-right">{selectedInstallment} x {formatPrice(currentInstallment.monthly)}</div>}
                </div>

                <Button onClick={handleSubmit} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-lg font-semibold" disabled={processing}>
                  {processing ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" />İşleniyor...</>) : (<>{formatPrice(currentInstallment.total)} Öde</>)}
                </Button>

                <div className="bg-green-50 rounded-lg p-3 flex items-start gap-2">
                  <Lock className="h-4 w-4 text-green-600 mt-0.5" />
                  <p className="text-xs text-green-700">Ödeme bilgileriniz 256-bit SSL şifreleme ile korunmaktadır.</p>
                </div>

                <div className="flex items-center justify-center gap-4 pt-2 border-t">
                  <div className="w-10 h-6 bg-gradient-to-r from-red-500 to-yellow-500 rounded"></div>
                  <div className="w-10 h-6 bg-blue-700 rounded flex items-center justify-center"><span className="text-white text-xs font-bold italic">VISA</span></div>
                  <div className="w-10 h-6 bg-blue-500 rounded flex items-center justify-center"><span className="text-white text-xs font-bold">TROY</span></div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
